// THE RICE ROWS, instanced — every crop row of every paddy in TWO InstancedMeshes (two draw calls however many paddies): the
// DIKES (the earth mounds, which never change) and the RICE BLADES on top of them, which grow with the season's clicks — that is
// what lets the field be the progress bar (src/game/riceGrowth.js). The kit's crop-row piece is split once at load: every triangle
// at or below the mound top is dike, everything above is blades (rooted on the mound top, so they scale about their roots).
// paddy.js hands each paddy's rows here instead of adding them to the tile; the blades grow a notch per click, in round-robin order
// across the paddies, ease toward their target height every frame, carry the season's tint (green → gold in HARVEST), darken with
// the dikes when the paddy is scorched, and flash briefly ("hot") when a spark or a crew worker passes.
import * as THREE from 'three'
import { Events, on, emit } from '../ui/eventBus.js'
import { RiceGrowth, PlotTypes, Seasons, LoopFeedback, ActiveField } from '../config/balance.js'
import { getPiece, paletteMaterial } from './synty.js'
import { registerPaddyRows, unregisterPaddyRows } from './paddyRows.js'
import { GROUND_Y, HEX_SIZE } from './hexGrid.js'
import { active } from '../game/state.js'
import { getSeasonClicks } from '../game/seasonManager.js'
import { countByType, getHomeTileIndex, tileToWorld } from '../game/plotState.js'
import { rowOrder, rowProgress, rowScale, rowTint, currentRowOrder, growthTarget, ROWS_PER_PADDY } from '../game/riceGrowth.js'

const CAP = 127 * ROWS_PER_PADDY
const ROW_HALF_L_M = 3.97, ROW_HALF_W_M = 0.73 // the Env_CropRow piece (metres at source scale)
const MOUND_TOP_M = 0.31 // the dike's top: the blades are rooted here (see the vertex histogram in the V1.17 notes)
const STAND_OFF_M = 0.6, ROW_INSET_M = 0.8 // a citizen pulling a plant stands this far from the row's centre line, this far in from its ends (metres at source scale)
const _m = new THREE.Matrix4(), _t = new THREE.Matrix4(), _c = new THREE.Color(), _v = new THREE.Vector3()

// Split the crop-row piece into dike and blades, by triangle, measured in the piece's placed metres (its node matrix applied).
function splitRow(piece) {
  const g = piece.geo, pos = g.attributes.position, idx = g.index
  const n = idx ? idx.count : pos.count
  const at = (i) => (idx ? idx.getX(i) : i)
  const y = (vi) => _v.fromBufferAttribute(pos, vi).applyMatrix4(piece.m).y
  const dike = [], blade = []
  for (let i = 0; i < n; i += 3) { const a = at(i), b = at(i + 1), c = at(i + 2); const top = Math.max(y(a), y(b), y(c)); (top <= MOUND_TOP_M + 0.005 ? dike : blade).push(a, b, c) }
  const pick = (list) => { // a fresh non-indexed geometry with the listed vertices (all attributes copied)
    const out = new THREE.BufferGeometry()
    for (const [name, attr] of Object.entries(g.attributes)) { const arr = new Float32Array(list.length * attr.itemSize); for (let k = 0; k < list.length; k++) for (let j = 0; j < attr.itemSize; j++) arr[k * attr.itemSize + j] = attr.getComponent(list[k], j); out.setAttribute(name, new THREE.BufferAttribute(arr, attr.itemSize)) }
    return out
  }
  return { dike: pick(dike), blade: pick(blade) }
}

export function createRiceField({ scene }) {
  let mesh = null, dikes = null, piece = null // mesh = the blades (grow); dikes = the mounds (fixed)
  const paddies = new Map() // key → { wrap, rows: [{ lx, lz, i, cur, hot, sy }], reg, burnt }
  let order = []            // paddies in a stable order (by key) — index = paddyIdx for the fill order
  const free = []           // instance slots not in use
  let nextSlot = 0
  let dirty = true          // recompute the whole field (a paddy added/removed, the season changed)
  let marker = null         // the next-row marker: pixel sparkles twinkling along the row the next click fills (THREE.Points)
  let markerK = null        // which row the sparkles are laid along
  let surplusMode = false   // every row is full this season: extra clicks still count (the harvest), the blades shimmer gold
  let sweepAt = 0           // the season-turn sweep: rows bump one after another in fill order

  function ensureMesh() {
    if (mesh) return true
    piece = getPiece('Env_CropRow'); const mat = paletteMaterial(piece?.tex ?? 1)
    if (!piece || !mat) return false
    const parts = splitRow(piece)
    const make = (geo, name) => { const im = new THREE.InstancedMesh(geo, mat, CAP); im.instanceMatrix.setUsage(THREE.DynamicDrawUsage); im.frustumCulled = false; im.count = 0; for (let i = 0; i < CAP; i++) im.setColorAt(i, _c.setRGB(1, 1, 1)); im.raycast = () => {}; im.name = name; scene.add(im); return im }
    mesh = make(parts.blade, 'rice-rows')
    dikes = make(parts.dike, 'rice-dikes')
    marker = makeTwinkle()
    scene.add(marker)
    return true
  }
  const slot = () => (free.length ? free.pop() : nextSlot < CAP ? nextSlot++ : -1)

  // A paddy's rows: `rows` are the row centres in the wrap's local metres (as the recipe places them). Returns false if the rows
  // cannot be instanced (the kit is not ready) — the caller then keeps the tile's own row meshes.
  function registerPaddy(key, wrap, rows) {
    if (!ensureMesh() || paddies.has(key)) return false
    const list = rows.map((r) => ({ lx: r.lx, lz: r.lz, i: slot(), cur: null, hot: 0, sy: 1 })).filter((r) => r.i >= 0)
    const reg = registerPaddyRows(wrap, list) // (the mound heights the characters walk over follow r.sy)
    paddies.set(key, { wrap, rows: list, reg, burnt: false })
    reorder()
    dirty = true
    return true
  }
  // The fill order starts with the paddies nearest home (where the camera usually is), so the first rows to grow are the ones in view.
  function reorder() {
    const hk = getHomeTileIndex(); const home = hk != null ? tileToWorld(hk) : { x: 0, z: 0 }
    const d = (key) => { const p = paddies.get(key); return Math.hypot(p.wrap.position.x - home.x, p.wrap.position.z - home.z) }
    order = [...paddies.keys()].sort((a, b) => d(a) - d(b) || a.localeCompare(b))
  }
  function unregisterPaddy(key) {
    const p = paddies.get(key); if (!p) return
    for (const r of p.rows) { free.push(r.i); mesh.setMatrixAt(r.i, _m.makeScale(0, 0, 0)); dikes.setMatrixAt(r.i, _m) }
    unregisterPaddyRows(p.wrap)
    paddies.delete(key)
    reorder()
    dirty = true
    if (mesh) { mesh.instanceMatrix.needsUpdate = true; dikes.instanceMatrix.needsUpdate = true }
  }

  // Dev/test: for every row, the x-scale written into the dike and blade instances, the blade's y-scale, and the expected x-scale
  // (the paddy's base scale × the piece's own). A row left at a mid-pop size shows up as dike ≠ expect.
  const _dp = new THREE.Vector3(), _dq = new THREE.Quaternion(), _ds = new THREE.Vector3()
  function rowScales() {
    if (!mesh) return []
    const out = []
    _t.copy(piece.m).decompose(_dp, _dq, _ds); const pieceSx = _ds.x
    for (const key of order) {
      const p = paddies.get(key); const base = p.wrap.userData.baseScale ?? p.wrap.scale.x
      for (const r of p.rows) {
        dikes.getMatrixAt(r.i, _t); _t.decompose(_dp, _dq, _ds); const dike = _ds.x
        mesh.getMatrixAt(r.i, _t); _t.decompose(_dp, _dq, _ds)
        out.push({ key, i: r.i, dike: +dike.toFixed(4), blade: +_ds.x.toFixed(4), bladeY: +_ds.y.toFixed(4), expect: +(base * pieceSx).toFixed(4), cur: r.cur == null ? null : +r.cur.toFixed(3), bump: +(r.bump ?? 0).toFixed(3), inScene: !!p.wrap.parent, wrapS: +p.wrap.scale.x.toFixed(4), base: p.wrap.userData.baseScale == null ? null : +p.wrap.userData.baseScale.toFixed(4), popping: !!p.popping })
      }
    }
    return out
  }
  // Where a citizen at (x, z) should stand to pull a PLANT — beside the nearest row (within `reach` world units), inside the blades'
  // extent, facing the row — so a kneel is always over rice, never over bare dirt. null when no row is within reach.
  function nearestPlantSpot(x, z, reach = 1.0) {
    let best = null
    for (let pi = 0; pi < order.length; pi++) {
      const key = order[pi], p = paddies.get(key), s = p.wrap.userData.baseScale ?? p.wrap.scale.x, inset = ROW_INSET_M * s
      for (let ri = 0; ri < p.rows.length; ri++) {
        const w = rowWorld(p, p.rows[ri])
        const zc = Math.max(w.z0 + inset, Math.min(w.z1 - inset, z))
        const dx = x - w.x, d = Math.hypot(dx, z - zc)
        if (d <= reach && (!best || d < best.d)) best = { d, x: w.x, z: zc, side: dx >= 0 ? 1 : -1, key, ri, s, y: w.y }
      }
    }
    if (!best) return null
    const side = best.side
    return { x: best.x + side * STAND_OFF_M * best.s, z: best.z, face: Math.atan2(-side, 0), key: best.key, ri: best.ri, rowX: best.x, y: best.y }
  }
  // Row k of the fill order → its paddy and row (rowOrder(pi, ri, n) = ri·n + pi).
  function rowByOrder(k) {
    const n = order.length; if (!n || k == null || k < 0) return null
    const pi = k % n, ri = Math.floor(k / n), p = paddies.get(order[pi]); const r = p?.rows[ri]
    return r ? { p, r, pi, ri } : null
  }
  // A paw click landed (Events.Click, after the count moved): the row that took the notch bumps; a row that just filled is announced;
  // past a full field the click is SURPLUS (it still counts toward the harvest — say so, and shimmer).
  function noteClick() {
    const n = order.length; if (!n) return
    const c = clicks(), t = target()
    const kNow = currentRowOrder(c, n, RiceGrowth), kBefore = currentRowOrder(c - 1, n, RiceGrowth)
    const hit = rowByOrder(kBefore)
    if (hit) hit.r.bump = Math.max(hit.r.bump ?? 0, kNow !== kBefore ? 1 : 0.6)
    if (kBefore != null && kNow !== kBefore) emit(Events.RowFilled, { k: kBefore, rows: n * ROWS_PER_PADDY, full: kNow == null })
    if (t > 0 && c > t) {
      emit(Events.Surplus, { surplus: c - t, target: t })
      const any = rowByOrder(Math.floor(Math.random() * n * ROWS_PER_PADDY)); if (any) any.r.bump = Math.max(any.r.bump ?? 0, 0.5)
    }
  }
  on(Events.Click, noteClick)
  // ── the ACTIVE FIELD rim: a soft glowing strip along the hex edge of the paddy being maintained ──
  let lastClickAt = -1e9
  const rims = new Map() // paddy key → { mesh, level }
  const noteActive = () => { lastClickAt = performance.now() }
  on(Events.Click, noteActive); on(Events.CitizenTended, noteActive)
  function makeRim() { // a hex ring: 6 quads between radius R−width and R, the same vertex angles as the tile outline
    const R = HEX_SIZE, r = HEX_SIZE - ActiveField.width, pos = [], idx = []
    for (let i = 0; i < 6; i++) { const a = (i * Math.PI) / 3; pos.push(Math.sin(a) * R, 0, Math.cos(a) * R, Math.sin(a) * r, 0, Math.cos(a) * r) }
    for (let i = 0; i < 6; i++) { const a = i * 2, b = ((i + 1) % 6) * 2; idx.push(a, b, a + 1, b, b + 1, a + 1) }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(idx)
    const m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: ActiveField.color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }))
    m.visible = false; m.raycast = () => {}; m.renderOrder = 1; m.name = 'active-rim'; scene.add(m)
    return m
  }
  function tickRims(dt, now) {
    const cur = currentRowCentre(), hot = now - lastClickAt < ActiveField.holdMs
    const activeKey = hot && cur ? cur.key : null
    for (const [key, p] of paddies) {
      let e = rims.get(key)
      if (!e) { if (key !== activeKey) continue; e = { mesh: makeRim(), level: 0 }; rims.set(key, e) }
      const target = key === activeKey ? 1 : 0
      e.level += (target - e.level) * (1 - Math.exp(-(target > e.level ? ActiveField.fadeIn : ActiveField.fadeOut) * dt))
      if (e.level < 0.01 && target === 0) { e.mesh.visible = false; e.level = 0; continue }
      e.mesh.visible = true
      e.mesh.position.set(p.wrap.position.x, GROUND_Y + 0.02, p.wrap.position.z)
      e.mesh.material.opacity = e.level * ActiveField.opacity * (0.8 + 0.2 * Math.sin(now / 1000 * 4))
    }
    for (const [key, e] of rims) if (!paddies.has(key)) { scene.remove(e.mesh); rims.delete(key) } // (a paddy that is gone)
  }
  on(Events.CitizenTended, noteClick) // (a directed kneel is a notch too)
  on(Events.ClicksLost, ({ clicks }) => { const n = order.length; if (!n) return; dirty = true; const k = currentRowOrder(clicks, n, RiceGrowth); const hit = rowByOrder(k); if (hit) hit.r.bump = Math.max(hit.r.bump ?? 0, 0.5) }) // (the crow ate: the row shrinks back)
  // Row k of the fill order in world units — for the crow, the glint and a directed citizen.
  function rowCentre(k) {
    const hit = rowByOrder(k); if (!hit) return null
    const w = rowWorld(hit.p, hit.r)
    return { k, key: hit.p === paddies.get(order[hit.pi]) ? order[hit.pi] : null, ri: hit.ri, x: w.x, y: w.y, z0: w.z0, z1: w.z1, z: (w.z0 + w.z1) / 2, progress: rowProgress(k, clicks(), RiceGrowth) }
  }
  const currentRowCentre = () => rowCentre(currentRowOrder(clicks(), order.length, RiceGrowth))
  // The row with the most rice on it (progress ≥ min) — what a crow goes for. null when nothing is grown enough.
  function fullestRow(min = 0.2) {
    const n = order.length * ROWS_PER_PADDY; let best = null
    for (let k = 0; k < n; k++) { const r = rowCentre(k); if (r && r.progress >= min && (!best || r.progress > best.progress)) best = r }
    return best
  }
  const randomRow = () => { const n = order.length * ROWS_PER_PADDY; return n ? rowCentre(Math.floor(Math.random() * n)) : null }
  on(Events.SeasonChange, ({ previous }) => { if (!previous || !order.length) return; sweepAt = performance.now(); for (const p of paddies.values()) for (const r of p.rows) r.sweep = true })
  // The next-row marker: TWINKLE_N hard-edged pixel sparkles scattered along the row's length over the mound top, each blinking on
  // its own phase (a few bright at a time), additive so they read as glints. Laid out again whenever the next row changes.
  const TWINKLE_N = 30
  function makeTwinkle() {
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(TWINKLE_N * 3), 3))
    geo.setAttribute('aSize', new THREE.BufferAttribute(new Float32Array(TWINKLE_N), 1))
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0xfff1b8) } },
      vertexShader: 'attribute float aSize; varying float vOn; void main() { vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_Position = projectionMatrix * mv; gl_PointSize = aSize; vOn = aSize; }',
      fragmentShader: 'uniform vec3 uColor; varying float vOn; void main() { if (vOn < 0.5) discard; gl_FragColor = vec4(uColor, 1.0); }',
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    })
    const pts = new THREE.Points(geo, mat)
    pts.name = 'rice-marker'; pts.visible = false; pts.frustumCulled = false; pts.renderOrder = 2; pts.raycast = () => {}
    pts.userData.phase = Float32Array.from({ length: TWINKLE_N }, () => Math.random() * Math.PI * 2)
    pts.userData.speed = Float32Array.from({ length: TWINKLE_N }, () => 2.5 + Math.random() * 4)
    pts.userData.px = Float32Array.from({ length: TWINKLE_N }, () => 2 + Math.floor(Math.random() * 3)) // 2–4 px, whole pixels
    pts.userData.dpr = Math.min(3, window.devicePixelRatio || 1)
    return pts
  }
  function layTwinkle(nx) {
    const w = rowWorld(nx.p, nx.r), s = nx.p.wrap.userData.baseScale ?? nx.p.wrap.scale.x, pos = marker.geometry.attributes.position.array
    for (let i = 0; i < TWINKLE_N; i++) {
      const t = (i + 0.15 + Math.random() * 0.7) / TWINKLE_N
      pos[i * 3] = w.x + (Math.random() - 0.5) * ROW_HALF_W_M * 1.1 * s
      pos[i * 3 + 1] = w.y + 0.02 + Math.random() * 0.06
      pos[i * 3 + 2] = w.z0 + t * (w.z1 - w.z0)
    }
    marker.geometry.attributes.position.needsUpdate = true
    marker.userData.x = w.x; marker.userData.z = (w.z0 + w.z1) / 2
  }
  const season = () => active().season ?? Seasons.PREP
  const clicks = () => getSeasonClicks()[season()] ?? 0
  const paddyCount = () => order.length
  const target = () => growthTarget(countByType(PlotTypes.FARM), RiceGrowth)

  // Row i of paddy p, in world units: the row runs along z.
  function rowWorld(p, r) {
    const s = p.wrap.userData.baseScale ?? p.wrap.scale.x // (the wrap's uniform scale = metres → world; baseScale ignores the pop-in tween)
    return { x: p.wrap.position.x + r.lx * s, z0: p.wrap.position.z + (r.lz - ROW_HALF_L_M * 0.88) * s, z1: p.wrap.position.z + (r.lz + ROW_HALF_L_M * 0.88) * s, y: GROUND_Y + MOUND_TOP_M * s } // (the dike's top: the blades' roots)
  }
  // The row the next click goes to (world coords), or null.
  function currentRow() {
    const k = currentRowOrder(clicks(), paddyCount(), RiceGrowth)
    if (k == null) return null
    const pi = k % paddyCount(), ri = Math.floor(k / paddyCount())
    const p = paddies.get(order[pi]); const r = p?.rows[ri]
    return r ? { ...rowWorld(p, r), key: order[pi], ri } : null
  }
  // Where a click's spark should run: the row the click fills if it is on screen — otherwise the row on screen nearest the middle
  // of the view, so the player always sees their click land in the fields (the growth itself still goes to the real row).
  function sparkRow(inView) {
    const cur = currentRow()
    if (!cur) return null
    if (inView(cur.x, cur.y, (cur.z0 + cur.z1) / 2).ok) return cur
    let best = null
    for (const key of order) {
      const p = paddies.get(key)
      for (let ri = 0; ri < p.rows.length; ri++) {
        const w = rowWorld(p, p.rows[ri]); const v = inView(w.x, w.y, (w.z0 + w.z1) / 2)
        if (v.ok && (!best || v.centre < best.centre)) best = { ...w, key, ri, centre: v.centre }
      }
    }
    return best
  }
  // The row under a world point (within a row's width and length), or null.
  function rowAt(x, z) {
    for (const [key, p] of paddies) {
      const s = p.wrap.userData.baseScale ?? p.wrap.scale.x
      const dx = x - p.wrap.position.x, dz = z - p.wrap.position.z
      if (dx * dx + dz * dz > (36 * s) * s) continue
      for (let ri = 0; ri < p.rows.length; ri++) {
        const r = p.rows[ri]
        if (Math.abs(dx / s - r.lx) < ROW_HALF_W_M && Math.abs(dz / s - r.lz) < ROW_HALF_L_M) return { key, ri }
      }
    }
    return null
  }
  function markHot(key, ri, amount = 1) { const r = paddies.get(key)?.rows[ri]; if (r) r.hot = Math.max(r.hot, amount) }
  function setBurned(key, on) { const p = paddies.get(key); if (p && p.burnt !== on) { p.burnt = on; dirty = true } }
  function clearBurns() { for (const p of paddies.values()) if (p.burnt) { p.burnt = false; dirty = true } }

  // Every frame: ease each row toward its target height, write the matrices and tints that changed.
  function tick(dt) {
    if (!mesh || !order.length) return
    const sea = season(), c = clicks(), n = order.length, t = target(), now = performance.now()
    const k = 1 - Math.exp(-5 * dt), hotK = Math.exp(-4 * dt), bumpK = Math.exp(-LoopFeedback.bumpDecayPerSec * dt)
    const gold = t > 0 && c >= t // every row full: the blades shimmer gold while the clicks keep coming
    if (gold !== surplusMode) { surplusMode = gold; dirty = true }
    let anyM = false, anyC = false, anyD = false, maxSlot = 0
    for (let pi = 0; pi < n; pi++) {
      const p = paddies.get(order[pi]); const s = p.wrap.scale.x
      // A placed paddy pops in (paddy.js tweens the wrap's scale from 1 % up to its base): the rows follow the pop and are written
      // once more as it settles — never left at a mid-pop size until a click happens to move them (the V1.17 "paddies are small" bug).
      const base = p.wrap.userData.baseScale ?? s, popping = Math.abs(s - base) > 1e-4, settle = !popping && !!p.popping
      p.popping = popping
      for (let ri = 0; ri < p.rows.length; ri++) {
        const r = p.rows[ri]
        const ko = rowOrder(pi, ri, n)
        const prog = rowProgress(ko, c, RiceGrowth)
        const want = rowScale(sea, prog, RiceGrowth)
        if (r.cur == null) r.cur = want
        if (r.sweep && now - sweepAt >= ko * LoopFeedback.sweepStaggerMs) { r.sweep = false; r.bump = Math.max(r.bump ?? 0, 0.9) } // the season-turn sweep
        if (r.bump) { r.bump *= bumpK; if (r.bump < 0.004) r.bump = 0 }
        const moveNeeded = Math.abs(want - r.cur) > 1e-4 || !!r.bump
        if (Math.abs(want - r.cur) > 1e-4) r.cur += (want - r.cur) * k
        if (moveNeeded || dirty || popping || settle) {
          r.sy = 1 // the dike never changes height (characters walk over the full mound)
          // dike:   T(wrap) · S(wrap) · T(lx,0,lz) · pieceMatrix
          // blades: the same, but scaled in Y about the mound top (their roots):  … · T(0,top,0) · S(1, cur, 1) · T(0,−top,0) · pieceMatrix
          _m.makeTranslation(p.wrap.position.x, p.wrap.position.y, p.wrap.position.z).multiply(_t.makeScale(s, s, s)).multiply(_t.makeTranslation(r.lx, 0, r.lz))
          if (dirty || popping || settle) { dikes.setMatrixAt(r.i, _t.copy(_m).multiply(piece.m)); anyD = true }
          _m.multiply(_t.makeTranslation(0, MOUND_TOP_M, 0)).multiply(_t.makeScale(1, r.cur * (1 + LoopFeedback.bladeBump * (r.bump ?? 0)), 1)).multiply(_t.makeTranslation(0, -MOUND_TOP_M, 0)).multiply(piece.m) // (a notch pops the blades up for a moment)
          mesh.setMatrixAt(r.i, _m); anyM = true
        }
        const wasHot = r.hot > 0.01
        if (wasHot) r.hot *= hotK
        if (wasHot || dirty || moveNeeded || surplusMode) {
          const tint = rowTint(sea, prog); const burn = p.burnt ? 0.18 : 1; const h = 1 + r.hot * 0.45
          const g = surplusMode ? 0.5 + 0.5 * Math.sin(now / 1000 * 6 + ko * 0.7) : 0 // the gold shimmer past a full field
          mesh.setColorAt(r.i, _c.setRGB(tint[0] * burn * h * (1 + 0.16 * g), tint[1] * burn * h * (1 + 0.02 * g), tint[2] * burn * h * (1 - 0.32 * g))); anyC = true // (the blades take the season's tint…)
          if (dirty) dikes.setColorAt(r.i, _c.setRGB(burn, burn, burn))                                     // (…the dikes only the scorch)
        }
        if (r.i + 1 > maxSlot) maxSlot = r.i + 1
      }
    }
    tickRims(dt, now)
    const count = Math.max(maxSlot, nextSlot)
    if (mesh.count !== count) { mesh.count = count; dikes.count = count }
    // the next-row marker: pixel sparkles along the row the next click fills; gone when the field is full (or empty)
    const nk = currentRowOrder(c, n, RiceGrowth), nx = rowByOrder(nk)
    if (marker) {
      if (!nx) { marker.visible = false; markerK = null }
      else {
        if (markerK !== nk || !marker.visible) { layTwinkle(nx); markerK = nk; marker.visible = true; marker.userData.k = nk }
        const sz = marker.geometry.attributes.aSize.array, u = marker.userData, tt = now / 1000
        for (let i = 0; i < TWINKLE_N; i++) { const b = Math.sin(tt * u.speed[i] + u.phase[i]); sz[i] = b > 0.35 ? u.px[i] * u.dpr : 0 } // about a third lit at a time
        marker.geometry.attributes.aSize.needsUpdate = true
      }
    }
    if (anyM) mesh.instanceMatrix.needsUpdate = true
    if (anyC && mesh.instanceColor) mesh.instanceColor.needsUpdate = true
    if (dirty || anyD) dikes.instanceMatrix.needsUpdate = true
    if (dirty && dikes.instanceColor) dikes.instanceColor.needsUpdate = true
    dirty = false
  }

  on(Events.SeasonChange, () => { dirty = true })
  on(Events.KingdomChange, () => { dirty = true })

  const api = {
    rowScales, // dev/test: every row's written dike / blade scales vs. what they should be
    marker: () => (marker ? { visible: marker.visible, x: +(marker.userData.x ?? 0).toFixed(3), z: +(marker.userData.z ?? 0).toFixed(3), k: marker.visible ? (marker.userData.k ?? null) : null } : null),
    surplusMode: () => surplusMode,
    activeField: () => ({ key: (performance.now() - lastClickAt < ActiveField.holdMs ? currentRowCentre()?.key : null) ?? null, levels: Object.fromEntries([...rims].map(([k, e]) => [k, +e.level.toFixed(3)])) }),
    nearestPlantSpot, rowCentre, currentRowCentre, fullestRow, randomRow,
    registerPaddy, unregisterPaddy, currentRow, sparkRow, rowAt, markHot, setBurned, clearBurns, tick, target,
    paddyCentres: () => order.map((key) => { const p = paddies.get(key); return { key, x: p.wrap.position.x, z: p.wrap.position.z } }),
    rowCount: () => [...paddies.values()].reduce((s, p) => s + p.rows.length, 0),
    rows: () => { const n = order.length, c = clicks(), sea = season(); const out = []; order.forEach((key, pi) => paddies.get(key).rows.forEach((r, ri) => out.push({ key, ri, k: rowOrder(pi, ri, n), p: rowProgress(rowOrder(pi, ri, n), c, RiceGrowth), target: rowScale(sea, rowProgress(rowOrder(pi, ri, n), c, RiceGrowth), RiceGrowth), cur: r.cur, hot: +r.hot.toFixed(3), bump: +(r.bump ?? 0).toFixed(3), i: r.i, burnt: paddies.get(key).burnt }))); return out },
    mesh: () => mesh, dikes: () => dikes,
  }
  if (import.meta.env?.DEV) window.__rice = { ...api, step: (dt = 1 / 60, n = 1) => { for (let i = 0; i < n; i++) tick(dt) }, clicks, season, drawCalls: null }
  return api
}
